import React, { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

// MUI
import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import CardContent from "@mui/material/CardContent";
import CardActions from "@mui/material/CardActions";
import IconButton from "@mui/material/IconButton";

// MUI Icons
import CloseIcon from "@mui/icons-material/Close";

// Custom Components
import useAuthFormController from "@/components/useAuthFormController";
import { GridContainer } from "@/ui/Grid";
import FormContent from "@/components/FormContent";
import FormButton from "@/ui/Form/FormButton";
import UserContext from "@/components/UserContext/UserContext";
import LoginErrorBox, { LoginErrorType}  from "@/views/Auth/LoginErrorBox";
import SnackbarContext from "@/ui/SnackbarContext/SnackbarContext";

// Services
import { authService } from "@/services/authService";

// Types
import { LoginFormProps } from "@/@types/views/Auth";
import { DCFieldValue } from "@/@types/lib/dataController";
import { DoLoginResponseSuccess } from "@/@types/components/authFormController";

function LoginForm(props: LoginFormProps) {
  const userContext = useContext(UserContext);
  const snackbarContext = useContext(SnackbarContext);

  const { t } = useTranslation();
  const navigate = useNavigate();
  const { record, validation, fields, onFieldChange, doLogin } = useAuthFormController(props);

  const [ errorType, setErrorType ] = useState<LoginErrorType | null>(null);

  const { dc, form } = props;

  const ERRCODE_NOTFOUND = 4;
  const ERRCODE_REGISTERED = 5;
  const ERRCODE_INACTIVE = 6;
  const ERRCODE_GENERAL = 7;
  const ERRCODE_HPS_LICENCE = 8;
  const ERRCODE_DISABLED_HPS = 9;

  const handleFieldChange = (value: any, source: string) => {
    if (onFieldChange) {
      onFieldChange(value as DCFieldValue, source);
    }
  };

  const handleRegistration = () => {
    navigate("/register");
  };

  const handlePasswordReset = () => {
    navigate("/password-reset-request", { replace: true });
  };

  const handleSubmit = () => {
    if (doLogin) {
      doLogin().then((result) => {
        if (result && result.success) {
          setErrorType(null);
          if (userContext !== null) {
            const userData = (result as DoLoginResponseSuccess).user;
            userContext.setUserData(userData);
            const defaultUrl = userContext.getDefaultUrl(userData.organisation_id)
            navigate(defaultUrl);
          }
        } else if (result && result.validationPass === false) {
          setErrorType(null);
          // console.log(result.validation);
        }
      }).catch((err) => {
        console.warn(err);

        switch (err.errorCode) {
          case ERRCODE_REGISTERED:
            setErrorType(LoginErrorType.Registered);
            break;
          case ERRCODE_INACTIVE:
            setErrorType(LoginErrorType.Inactive);
            break;
          case ERRCODE_GENERAL:
            setErrorType(LoginErrorType.General);
            break;
          case ERRCODE_HPS_LICENCE:
            setErrorType(LoginErrorType.HPSLicence);
            break;
          case ERRCODE_DISABLED_HPS:
            setErrorType(LoginErrorType.HPSDisabled);
            break;
          case ERRCODE_NOTFOUND:
          default:
            setErrorType(null);
            break;
        }
      });
    }
  };

  const handleResendToken = () => {

    const email = record.email as string;
    const password = record.password as string;

    authService
          .resendRegisterToken("", email, password)
          .then((response) => { 
            snackbarContext.showNotification("action", 'success');
          })
          .catch((error) => { 
            snackbarContext.showNotification("error", 'error');
            console.log(error) 
          });
    // TODO: call API
    setErrorType(null);

  }

  const backHome = () => {
    navigate("/");
  };

  return (
    <Card>
      <CardHeader
        title={t("titles.login")}
        action={
          <IconButton onClick={backHome}>
            <CloseIcon />
          </IconButton>
        }
      />
      <CardContent>
        <form>
          <GridContainer>
            <FormContent
              fieldNames={dc.getFormFieldsNames(form)}
              record={record}
              validation={validation}
              fields={fields}
              onFieldChange={handleFieldChange}
              onSubmit={handleSubmit}
              mode="insert"
              columns={1}
            />
          </GridContainer>
        </form>
        { errorType ? 
          <LoginErrorBox 
            errorType={errorType}
            onResendToken={handleResendToken} /> 
          : null}
      </CardContent>
      <CardActions sx={{flexDirection: {xs: "column", md: "row"}, rowGap: 1}}>
        <FormButton  variant="contained" onClick={handleSubmit} sx={{mx: 1, width: {xs: "100%", md: "auto"}}}>
          {t("buttons.login")}
        </FormButton>
        <FormButton variant="text" onClick={handleRegistration} sx={{mx: 1, width: {xs: "100%", md: "auto"}}}>
          {t("buttons.register")}
        </FormButton>
        <FormButton variant="text" onClick={handlePasswordReset} sx={{mx: 1, width: {xs: "100%", md: "auto"}}}>
          {t("buttons.password_reset")}
        </FormButton>
      </CardActions>
    </Card>
  );
}

export default LoginForm;
